import { useCallback, useMemo } from 'react';

import {
  computeTileSizeChange,
  ORDERED_TILE_FONT_SIZE_VARIANTS,
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_WIDTH,
  TileFontSizeVariant,
} from '@/components/diagrams/components/Tile';
import { isTileNode } from '@/components/diagrams/FlowChart/utils/nodes';

import { useEntityDiagramContext } from '../contexts/entityDiagram.context';
import { BulkTileSizeChangeNodeChange } from '../types';

/**
 * Single-entity-diagram bulk tile size increase/decrease. Unlike the shared
 * `useBulkTileSizeChange`, this dispatches through the reducer so the scaled
 * `data.width` / `fontSizeVariant` land in `state.nodes` (the controlled
 * source), which is what makes the tile interior — not just the node box —
 * resize. Mirrors the estate waterfall's own reducer-dispatched version.
 */
export function useBulkTileSizeChange() {
  const { state, dispatch } = useEntityDiagramContext();

  const eligibleTiles = useMemo(
    () => state.nodes.filter(isTileNode).filter((node) => !!node.selected),
    [state.nodes]
  );

  const maxVariant =
    ORDERED_TILE_FONT_SIZE_VARIANTS[ORDERED_TILE_FONT_SIZE_VARIANTS.length - 1];
  const minVariant = ORDERED_TILE_FONT_SIZE_VARIANTS[0];

  const canIncrease = useMemo(
    () =>
      eligibleTiles.some(
        (node) =>
          (node.data.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT) !==
          maxVariant
      ),
    [eligibleTiles, maxVariant]
  );

  const canDecrease = useMemo(
    () =>
      eligibleTiles.some(
        (node) =>
          (node.data.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT) !==
          minVariant
      ),
    [eligibleTiles, minVariant]
  );

  const handleSizeChange = useCallback(
    (increment: boolean) => {
      // A tile at the variant limit for this direction can't step further.
      const limitVariant = increment ? maxVariant : minVariant;

      const nodeChanges = eligibleTiles
        .filter(
          (node) =>
            (node.data.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT) !==
            limitVariant
        )
        .map((node): BulkTileSizeChangeNodeChange | null => {
          // computeTileSizeChange gates on the variant range (returns null at
          // the limit) and yields the next variant; the scale factor itself is
          // derived from `increment` in the reducer, so newWidth is unused.
          const currentWidth =
            node.measured?.width ?? node.width ?? TILE_DEFAULT_WIDTH;
          const currentVariant = node.data.fontSizeVariant as
            | TileFontSizeVariant
            | undefined;

          const result = computeTileSizeChange(
            currentWidth,
            currentVariant,
            increment
          );
          if (!result) return null;

          return {
            nodeId: node.id,
            newFontSizeVariant: result.newFontSizeVariant,
          };
        })
        .filter((change): change is BulkTileSizeChangeNodeChange => !!change);

      if (nodeChanges.length === 0) return;

      dispatch({ type: 'BULK_TILE_SIZE_CHANGE', nodeChanges, increment });
    },
    [dispatch, eligibleTiles, maxVariant, minVariant]
  );

  const handleBulkIncrease = useCallback(
    () => handleSizeChange(true),
    [handleSizeChange]
  );
  const handleBulkDecrease = useCallback(
    () => handleSizeChange(false),
    [handleSizeChange]
  );

  return {
    eligibleTileCount: eligibleTiles.length,
    canIncrease,
    canDecrease,
    handleBulkIncrease,
    handleBulkDecrease,
  };
}
